import { useEffect, useState } from "react";
import { Menu, X, User } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useScrolled } from "@/hooks/useScrolled";
import { SIGNIN_OPEN_EVENT, openSignIn } from "@/lib/signInModal";
import { openPreRegister } from "@/lib/preRegisterModal";
import { SignInModal } from "@/components/auth/SignInModal";
import { PreRegisterModal } from "@/components/preregister/PreRegisterModal";
import { MobileNavMenu } from "@/components/layout/MobileNavMenu";

const NAV_LINKS = [
  { label: "How It Works", href: "#how" },
  { label: "Drivers", href: "#drivers" },
  { label: "Find a Driver", href: "/drivers" },
  { label: "Owner Operators", href: "#owner-operators" },
  { label: "Carriers", href: "#carriers" },
  { label: "Brokers", href: "/brokers" },
  { label: "Broker Portal", href: "/broker-portal" },
  { label: "RV Rescue", href: "#rv" },
  { label: "Safety & Trust", href: "#trust" },
  { label: "About", href: "#about" },
];

export const Header = () => {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInTab, setSignInTab] = useState("signin");
  const [signInRole, setSignInRole] = useState(null);
  const user = useAuth();

  useEffect(() => {
    const onOpenRequest = (e) => {
      setSignInTab(e.detail?.tab || "signin");
      setSignInRole(e.detail?.role || null);
      setSignInOpen(true);
    };
    window.addEventListener(SIGNIN_OPEN_EVENT, onOpenRequest);
    return () => window.removeEventListener(SIGNIN_OPEN_EVENT, onOpenRequest);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-rr-line shadow-sm" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[92px] flex items-center justify-between">
        <a href="#top" data-testid="logo-home-link" className="flex items-center gap-2 shrink-0 relative z-10">
          <img src="/assets/relayrigs-logo.png" alt="RelayRigs" className="h-20 w-auto -my-3" />
        </a>

        <nav className="hidden lg:flex items-center gap-5">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`text-sm font-semibold whitespace-nowrap transition-colors hover:text-rr-orange ${
                scrolled ? "text-rr-navy" : "text-white"
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link to="/account" data-testid="account-link" className={`flex items-center gap-2 text-sm font-bold px-4 py-2 hover:text-rr-orange transition-colors ${scrolled ? "text-rr-navy" : "text-white"}`}>
                <User size={16} /> {user.name || user.email}
              </Link>
              <Link to="/driver-portal" data-testid="driver-portal-link" className={`text-sm font-bold px-3 py-2 hover:text-rr-orange transition-colors ${scrolled ? "text-rr-navy" : "text-white"}`}>
                Driver Portal
              </Link>
            </>
          ) : (
            <button
              onClick={() => openSignIn("signin")}
              data-testid="signin-button"
              className={`text-sm font-bold px-4 py-2 whitespace-nowrap hover:text-rr-orange transition-colors ${scrolled ? "text-rr-navy" : "text-white"}`}
            >
              Sign In
            </button>
          )}
          <button
            onClick={() => openPreRegister("driver")}
            data-testid="header-join-cta"
            className="text-sm font-bold bg-rr-orange text-white px-5 py-3 rounded-full hover:bg-rr-navy transition-all hover:-translate-y-0.5 shadow-lg shadow-rr-orange/20"
          >
            Pre-Register
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className={`lg:hidden p-2 ${scrolled || open ? "text-rr-navy" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <MobileNavMenu
        open={open}
        onClose={() => setOpen(false)}
        navLinks={NAV_LINKS}
        user={user}
        onSignIn={() => openSignIn("signin")}
        onPreRegister={() => openPreRegister("driver")}
      />

      <SignInModal open={signInOpen} onOpenChange={setSignInOpen} initialTab={signInTab} initialRole={signInRole} />
      <PreRegisterModal />
    </header>
  );
};
